import React from 'react';

// @mui
import { Box, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
// hooks
import useMainStore from 'src/store';

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '100%',
  alignItems: 'center',
  paddingTop: theme.spacing(2.5),
}));

interface LocationSelectionRequiredProps {
  children: React.ReactNode;
  // Only allow a single location to be selected
  singleLocation?: boolean;
}

const shouldDisplayChildren = (singleLocation: boolean | undefined, selectedLocations: any[]) => {
  // Single location
  if (singleLocation && selectedLocations && selectedLocations.length === 1) {
    return true;
  }

  // Multiple locations
  if (!singleLocation && selectedLocations && selectedLocations.length > 0) {
    return true;
  }

  return false;
};

const generateLocationSelectionRequiredMessage = (singleLocation: boolean | undefined) => {
  if (singleLocation) {
    return {
      title: 'Single Location Selection Required',
      message: 'Please select only one location from the sidebar to view results.',
    };
  }

  return {
    title: 'Location Selection Required',
    message: 'Please select at least one location from the sidebar to view results.',
  };
};

const LocationSelectionRequired: React.FC<LocationSelectionRequiredProps> = ({
  children,
  singleLocation,
}) => {
  const selectedLocations = useMainStore((state) => state.selectedLocations);

  const { title, message } = generateLocationSelectionRequiredMessage(singleLocation);

  if (shouldDisplayChildren(singleLocation, selectedLocations ?? [])) {
    return <>{children}</>;
  }

  return (
    <RootStyle>
      <Container>
        <Box sx={{ maxWidth: 600, margin: 'auto', textAlign: 'center' }}>
          <Typography variant="h3" paragraph>
            {title}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>{message}</Typography>
        </Box>
      </Container>
    </RootStyle>
  );
};

export default LocationSelectionRequired;
